import { fetchEventSource } from '@microsoft/fetch-event-source';
import { getAuth0AccessTokenSilently } from '@store/auth0BaseQuery';

const API_BASE_URL = ((import.meta.env.VITE_API_URL as string) ?? '').replace(/\/$/, '');

/**
 * Coarse SSE event names from the artifact-level stream. Per the GDN-938
 * handoff: these are artifact-lifecycle signals, NOT per-DocRef. Each one
 * may trigger a refetch of derived caches (timeline, DocRef list, document
 * list) since that's where the user-visible state lives.
 */
export const ARTIFACT_EVENT_NAMES = [
  'artifact.ingested',
  'artifact.decomposed',
  'artifact.parsing',
  'artifact.parsed',
  'artifact.processed',
  'artifact.failed',
  'artifact.updated',
  'intake_hub.extraction_complete',
] as const;

export type ArtifactEventName = (typeof ARTIFACT_EVENT_NAMES)[number];

/**
 * Domain-side-effect event name (GDN-1032 follow-up). Fires after a
 * DocRef has been created AND a registered extractor has run against
 * it (e.g. the insurance-card vision extractor). Carries the extracted
 * summary so the FE can render a "review & save" affordance without
 * waiting for the next DocRef detail fetch.
 */
export const DOCREF_EXTRACTION_EVENT = 'document_reference.extraction_ready' as const;

/**
 * Lifecycle events that materially change what derived views (timeline,
 * documents list) should show. Pre-decomposition events (`ingested`,
 * `parsing`) don't shift the list — the artifact already exists; clinical
 * fields haven't landed yet.
 */
export const ARTIFACT_INVALIDATING_EVENTS = new Set<ArtifactEventName>([
  'artifact.decomposed',
  'artifact.parsed',
  'artifact.processed',
  'artifact.failed',
  'artifact.updated',
  'intake_hub.extraction_complete',
]);

/**
 * Minimal DocRef head attached to enriched artifact lifecycle events so the
 * FE can swap the ghost card to the extracted title in-place, without
 * waiting for the list refetch to arrive.
 */
export interface ArtifactEventDocumentReference {
  id: string;
  title: string;
  docKind: string | null;
}

/**
 * Parsed payload from an artifact SSE event. The BE sends a full
 * artifact summary; we narrow to the fields the FE actually consumes.
 */
export interface ArtifactEvent {
  name: ArtifactEventName;
  artifactId: string;
  /** processing_status from the BE (`uploaded` / `parsing` / `parsed` /
   *  `decomposed` / `processed` / `failed`). Drives the ghost caption. */
  status: string;
  /** Present on enriched lifecycle events. Empty array means Fast Card
   *  ran but produced no clinical records (e.g. an admin contract). */
  documentReferences?: ArtifactEventDocumentReference[];
}

/**
 * Insurance-card extraction summary as returned by the BE on
 * `document_reference.extraction_ready`. Loosely typed (BE schema
 * doesn't model the per-kind shape today) so we narrow at the call
 * site as needed.
 */
export interface DocRefExtractionEvent {
  documentReferenceId: string;
  artifactId: string;
  /** Which extractor ran — e.g. `"insurance_card"`. */
  kind: string;
  /** Extracted fields. Shape varies per kind — read with care. */
  summary: Record<string, unknown>;
}

interface SubscribeOptions {
  /** Aborted on cleanup to tear down the SSE connection. */
  controller: AbortController;
  /** Fires for each invalidating artifact event — drives RTK cache
   *  refetches for the timeline / docs list. */
  onInvalidate: (event: ArtifactEventName) => void;
  /** Fires for every artifact event with the parsed payload. Used by
   *  the optimistic-upload state to advance ghost captions through the
   *  pipeline and dismiss them on terminal events. */
  onEvent?: (event: ArtifactEvent) => void;
  /** Fires when a domain extractor has produced a result against a
   *  freshly-created DocRef. Use to invalidate the DocRef detail and
   *  surface the "review extraction" affordance. */
  onDocRefExtraction?: (event: DocRefExtractionEvent) => void;
  /** Fires `true` once the stream is open and `false` whenever it drops.
   *  Consumers use it to decide how hard their polling fallback must work. */
  onConnectionChange?: (connected: boolean) => void;
  /** Fires each time the stream re-opens after a drop. Redis pub/sub does not
   *  replay, so events published while disconnected are gone — refetch. */
  onReconnect?: () => void;
}

interface ArtifactEventPayload {
  artifact_id?: string;
  processing_status?: string;
  /** Set on enriched lifecycle events after Fast Card creates DocRefs. */
  document_references?: Array<{
    document_reference_id?: string;
    title?: string;
    doc_kind?: string | null;
  }>;
}

interface DocRefExtractionPayload {
  document_reference_id?: string;
  artifact_id?: string;
  kind?: string;
  summary?: Record<string, unknown>;
}

function parseDocRefExtractionEvent(data: string): DocRefExtractionEvent | null {
  try {
    const payload = JSON.parse(data) as DocRefExtractionPayload;
    if (!payload.document_reference_id || !payload.artifact_id || !payload.kind) {
      return null;
    }
    return {
      documentReferenceId: payload.document_reference_id,
      artifactId: payload.artifact_id,
      kind: payload.kind,
      summary: payload.summary ?? {},
    };
  } catch {
    return null;
  }
}

function parseArtifactEvent(name: ArtifactEventName, data: string): ArtifactEvent | null {
  try {
    const payload = JSON.parse(data) as ArtifactEventPayload;
    if (!payload.artifact_id || !payload.processing_status) return null;
    return {
      name,
      artifactId: payload.artifact_id,
      status: payload.processing_status,
      documentReferences: payload.document_references
        ?.filter(d => d.document_reference_id && d.title)
        .map(d => ({
          id: d.document_reference_id ?? '',
          title: d.title ?? '',
          docKind: d.doc_kind ?? null,
        })),
    };
  } catch {
    return null;
  }
}

/** First reconnect delay; doubles per consecutive failure up to the cap. */
export const ARTIFACT_STREAM_RETRY_BASE_MS = 1000;
export const ARTIFACT_STREAM_RETRY_MAX_MS = 30 * 1000;
/** A token that is still rejected after this many forced refreshes is not
 *  going to start working; stop instead of hammering the API. */
const MAX_CONSECUTIVE_UNAUTHORIZED = 3;

/** Thrown for failures a retry cannot fix (forbidden, no session). */
class FatalArtifactStreamError extends Error {}

/**
 * Auth0 error codes that mean the session is over and only an interactive
 * login can produce a token again. Anything else from the token call is
 * treated as transient.
 */
const SESSION_ENDING_TOKEN_ERROR_CODES: ReadonlySet<string> = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'missing_refresh_token',
  'invalid_grant',
]);

/** Auth0 SDK and API errors both carry the OAuth code on `error`. */
export function isSessionEndingTokenError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('error' in error)) return false;
  const code: unknown = error.error;
  return typeof code === 'string' && SESSION_ENDING_TOKEN_ERROR_CODES.has(code);
}

/** Thrown before any request is made: the tab is hidden, or an attempt is
 *  already open. Not a failure — it only parks the library's retry timer. */
class DeferredArtifactStreamAttempt extends Error {}

/**
 * Exponential backoff with jitter: between half and all of
 * `base * 2^(failures-1)`, capped, so a fleet of tabs dropped by one deploy
 * does not reconnect in lockstep.
 */
export function artifactStreamRetryDelay(
  consecutiveFailures: number,
  random: () => number = Math.random,
): number {
  const exponent = Math.max(0, consecutiveFailures - 1);
  const ceiling = Math.min(
    ARTIFACT_STREAM_RETRY_BASE_MS * 2 ** exponent,
    ARTIFACT_STREAM_RETRY_MAX_MS,
  );
  return Math.round(ceiling * (0.5 + random() * 0.5));
}

/**
 * Open an artifact SSE stream and keep it open until the controller aborts.
 *
 * `fetchEventSource` only retries when something throws. A response that ends
 * normally — the server's 30-minute cap (`stream.closed`), a graceful deploy,
 * the body of a 401 — resolves the promise and the stream is gone for the life
 * of the page. So a clean close is turned into a retriable error here, the
 * token is read per attempt (the library copies headers once), and a 401
 * forces a token refresh before the next attempt.
 *
 * Bounded: jittered backoff up to `ARTIFACT_STREAM_RETRY_MAX_MS`, a stop on
 * 403 / repeated 401 / no session, and no request while the tab is hidden —
 * the library aborts on hide and reconnects itself when the tab is shown.
 */
async function subscribeToArtifactEventStream(
  url: string,
  {
    controller,
    onInvalidate,
    onEvent,
    onDocRefExtraction,
    onConnectionChange,
    onReconnect,
  }: SubscribeOptions,
): Promise<void> {
  let forceTokenRefresh = false;
  let consecutiveFailures = 0;
  let consecutiveUnauthorized = 0;
  let hasConnected = false;
  // The library can start a second attempt while one is open (its retry timer
  // is not cleared when a visibility change reconnects). Let only one through.
  let attemptOpen = false;
  let endOpenAttempt: (() => void) | undefined;

  await fetchEventSource(url, {
    method: 'GET',
    signal: controller.signal,
    async fetch(input, init) {
      if (document.hidden || attemptOpen) throw new DeferredArtifactStreamAttempt();
      attemptOpen = true;
      // Teardown cannot rely on the library's per-attempt signal: a deferred
      // attempt replaces the library's controller, after which neither
      // tab-hide nor the caller's abort would reach this open request.
      const attempt = new AbortController();
      const endAttempt = () => attempt.abort();
      endOpenAttempt = endAttempt;
      const endAttemptWhenHidden = () => {
        if (document.hidden) attempt.abort();
      };
      init?.signal?.addEventListener('abort', endAttempt, { once: true });
      controller.signal.addEventListener('abort', endAttempt, { once: true });
      document.addEventListener('visibilitychange', endAttemptWhenHidden);
      attempt.signal.addEventListener(
        'abort',
        () => {
          init?.signal?.removeEventListener('abort', endAttempt);
          controller.signal.removeEventListener('abort', endAttempt);
          document.removeEventListener('visibilitychange', endAttemptWhenHidden);
          attemptOpen = false;
          onConnectionChange?.(false);
        },
        { once: true },
      );
      if (init?.signal?.aborted || controller.signal.aborted) attempt.abort();
      let token: string;
      try {
        token = await getAuth0AccessTokenSilently(
          forceTokenRefresh ? { cacheMode: 'off' } : undefined,
        );
      } catch (tokenError) {
        // Only a finished session is final. A network error, a timeout, an
        // Auth0 5xx or Auth0 not being mounted yet is retried with the same
        // backoff as any other failed attempt; `forceTokenRefresh` is left
        // set so a refresh owed after a 401 is still made on the next try.
        if (isSessionEndingTokenError(tokenError)) {
          throw new FatalArtifactStreamError('No session for artifact stream');
        }
        throw new Error('Artifact stream token unavailable; retrying');
      }
      forceTokenRefresh = false;
      return window.fetch(input, {
        ...init,
        signal: attempt.signal,
        headers: { ...(init?.headers as Record<string, string>), Authorization: `Bearer ${token}` },
      });
    },
    // Wrapped so a throw becomes a rejection, which is what the library retries on.
    onopen: res =>
      Promise.resolve().then(() => {
        if (res.ok) {
          consecutiveFailures = 0;
          consecutiveUnauthorized = 0;
          onConnectionChange?.(true);
          if (hasConnected) onReconnect?.();
          hasConnected = true;
          return;
        }
        if (res.status === 401) {
          consecutiveUnauthorized += 1;
          if (consecutiveUnauthorized >= MAX_CONSECUTIVE_UNAUTHORIZED) {
            throw new FatalArtifactStreamError('Artifact stream unauthorized');
          }
          forceTokenRefresh = true;
          throw new Error('Artifact stream unauthorized; retrying with a fresh token');
        }
        if (res.status >= 400 && res.status < 500) {
          throw new FatalArtifactStreamError(`Artifact stream rejected: ${res.status}`);
        }
        throw new Error(`Artifact stream failed: ${res.status}`);
      }),
    onmessage(ev) {
      if (!ev.event) return;
      // DocRef-level extraction-ready event has its own shape — handle
      // separately from the artifact-lifecycle stream.
      if (ev.event === DOCREF_EXTRACTION_EVENT) {
        if (!onDocRefExtraction) return;
        const parsed = parseDocRefExtractionEvent(ev.data);
        if (parsed) onDocRefExtraction(parsed);
        return;
      }
      if (!ARTIFACT_EVENT_NAMES.includes(ev.event as ArtifactEventName)) {
        return;
      }
      const name = ev.event as ArtifactEventName;
      if (onEvent) {
        const parsed = parseArtifactEvent(name, ev.data);
        if (parsed) onEvent(parsed);
      }
      if (ARTIFACT_INVALIDATING_EVENTS.has(name)) onInvalidate(name);
    },
    onclose() {
      throw new Error('Artifact stream closed by server');
    },
    onerror(err) {
      if (err instanceof DeferredArtifactStreamAttempt) return ARTIFACT_STREAM_RETRY_MAX_MS;
      // Releases the attempt's listeners, clears `attemptOpen` and reports
      // the drop — every non-deferred error comes from an attempt that opened.
      endOpenAttempt?.();
      if (err instanceof FatalArtifactStreamError) throw err;
      consecutiveFailures += 1;
      return artifactStreamRetryDelay(consecutiveFailures);
    },
  }).catch(() => {});
}

/**
 * Subscribe to the patient-scoped artifact event stream. Resolves once the
 * stream ends (the controller aborted, or a failure no retry can fix).
 */
export function subscribeToPatientArtifactEvents(
  patientId: string,
  options: SubscribeOptions,
): Promise<void> {
  return subscribeToArtifactEventStream(
    `${API_BASE_URL}/v2/provider/patients/${patientId}/artifacts/events`,
    options,
  );
}

/** Sibling for the org-scoped event stream — same shape, different URL. */
export function subscribeToOrganizationArtifactEvents(
  organizationId: string,
  options: SubscribeOptions,
): Promise<void> {
  return subscribeToArtifactEventStream(
    `${API_BASE_URL}/v2/provider/organizations/${organizationId}/artifacts/events`,
    options,
  );
}

/** Admin org-scoped event stream. Same payload shape, admin auth surface. */
export function subscribeToAdminOrganizationArtifactEvents(
  organizationId: string,
  options: SubscribeOptions,
): Promise<void> {
  return subscribeToArtifactEventStream(
    `${API_BASE_URL}/v2/admin/organizations/${organizationId}/artifacts/events`,
    options,
  );
}
